import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { appUrl } from "../../../../../utils/config";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import EditIcon from "@material-ui/icons/Edit";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "900px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const CampaignStatusTab = (props) => {
  const { featuresAccess } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [campaignStatuses, setCampaignStatuses] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  // Map data from API response - no API calls, no loaders when dropdown changes
  useEffect(() => {
    setError(null);
    // Map data from API response (apiData from parent)
    if (props.apiData?.campaign && props.apiData.campaign.length > 0) {
      const list = props.apiData.campaign || [];
      const formattedStatuses = list.map((item, index) => {
        const statusType = item.statusType || item.status_type || "";
        return {
          id: item.statusID || item.status_id || `ID-${index + 1}`,
          name: item.statusName || item.status_name || "",
          selected: !!item.activated,
          type: statusType === "new" || statusType === "New" ? "New" : statusType,
          isNew: false,
          newStatusName: item.newStatusName || null,
          lastUpdatedBy: item.lastUpdatedBy || item.last_updated_by,
        };
      });
      setCampaignStatuses(formattedStatuses);
    } else {
      setCampaignStatuses([]);
    }
  }, [props.apiData?.campaign]);

  // Use parent's loading state (only shows when API is called)
  const isLoading = props.isLoading || false;

  const addCampaignStatus = async (index) => {
    const statusToAdd = campaignStatuses[index];
    if (!statusToAdd.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        projectDevelopmentStatuses: [{
          statusType: "new",
          statusName: statusToAdd.name.trim(),
          activated: statusToAdd.selected,
          objectType: "Campaign"
        }]
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addPDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        // Update the status in local state to mark it as saved
        setCampaignStatuses((prev) =>
          prev.map((status, i) =>
            i === index
              ? { ...status, id: response.data?.data?.statusID || response.data?.data?.id || status.id, isNew: false, type: "New" }
              : status
          )
        );
        if (props.onRefresh) {
          props.onRefresh();
        }
        if (props.setCampaignStatus) {
          props.setCampaignStatus((prev) => !prev);
        }
      } else {
        setError(response.data?.message || "Failed to add campaign status.");
      }
    } catch (error) {
      console.error("Error adding campaign status:", error);
      setError("An error occurred while adding the campaign status.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleSaveChanges = async () => {
    try {
      const isAnyStatusSelected = campaignStatuses.filter((status) => status.isUpdated);
      if (!isAnyStatusSelected.length) return;
      
      if (campaignStatuses.every((status) => !status.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }
      setErrorMessage("");

      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        projectStatuses: [],
        campaignStatuses: isAnyStatusSelected.map((status) => ({
          statusID: status.id,
          statusName: status.name,
          statusType: status.type,
          activated: status.selected,
          markForDelete: false,
          newStatusName: status.newStatusName || null,
        })),
        changeControlStatuses: [],
        productReleaseTestStatuses: [],
        inProcessTestStatuses: [],
        deviationStatuses: [],
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updatePDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        if (props.onRefresh) {
          props.onRefresh();
        }
        setIsUpdated(false);
      } else {
        setErrorMessage(response.data?.message || "Failed to update campaign statuses.");
      }
    } catch (error) {
      console.error("Error updating campaign statuses:", error);
      setErrorMessage("An error occurred while updating campaign statuses. Please try again.");
    }
  };

  const handleDelete = async (index) => {
    try {
      const statusToDelete = campaignStatuses[index];
      if (statusToDelete.isNew) {
        setCampaignStatuses((prev) => prev.filter((_, i) => i !== index));
        return;
      }

      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        projectStatuses: [],
        campaignStatuses: [{
          statusID: statusToDelete.id,
          statusName: statusToDelete.name,
          statusType: statusToDelete.type,
          activated: statusToDelete.selected,
          markForDelete: true,
          newStatusName: statusToDelete.newStatusName || null,
        }],
        changeControlStatuses: [],
        productReleaseTestStatuses: [],
        inProcessTestStatuses: [],
        deviationStatuses: [],
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updatePDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        if (props.onRefresh) {
          props.onRefresh();
        }
        if (props.setCampaignStatus) {
          props.setCampaignStatus((prev) => !prev);
        }
      } else {
        setError(response.data?.message || "Failed to delete campaign status.");
      }
    } catch (error) {
      console.error("Error deleting campaign status:", error);
      setError("An error occurred while deleting the campaign status.");
    }
  };

  const handleToggle = (index) => {
    const updated = [...campaignStatuses];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setCampaignStatuses(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...campaignStatuses];
    updated[index].name = value;
    setCampaignStatuses(updated);
    setIsUpdated(true);
  };

  const handleResetChanges = () => {
    if (isUpdated) {
      if (props.onRefresh) {
        props.onRefresh();
      }
      setIsUpdated(false);
      setErrorMessage("");
    }
  };

  const handleAddStatus = () => {
    setCampaignStatuses([
      ...campaignStatuses,
      {
        name: "",
        selected: false,
        isNew: true,
      },
    ]);
  };

  const handleCancel = (index) => {
    const statusToCancel = campaignStatuses[index];
    if (statusToCancel.isNew) {
      setCampaignStatuses((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setCampaignStatuses((prev) =>
      prev.map((status, i) =>
        i === index
          ? {
              ...status,
              isEditing: false,
              name: status.originalName || status.name,
            }
          : status
      )
    );
  };

  return (
    <div className={classes.container} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
        <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Selection</TableCell>
              <TableCell className={classes.headerCell}>Campaign Status</TableCell>
              <TableCell className={classes.headerCell} style={{ whiteSpace: "nowrap" }}>
                Renamed Campaign Status
              </TableCell>
              <TableCell className={classes.headerCell} style={{ whiteSpace: "nowrap" }}>
                Campaign Status ID
              </TableCell>
              <TableCell className={classes.headerCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", backgroundColor: "#ffecec", color: "#b00020", border: "1px solid #ff9999" }}>
                    <Typography>{error}</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : campaignStatuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", color: "#666" }}>
                    <Typography>No Campaign Statuses Found</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              campaignStatuses.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={item.selected}
                      onChange={() => handleToggle(index)}
                      classes={{ switchBase: classes.switchBase }}
                      disabled={!canEdit}
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Text Here"
                          value={item.name}
                          onChange={(e) => handleNameChange(index, e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                              if (!allowedChars.test(e.data)) {
                                e.preventDefault();
                              }
                            }
                          }}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton onClick={() => handleCancel(index)} className={classes.iconBtn}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => addCampaignStatus(index)}
                            className={classes.iconBtn}
                            disabled={!item.name?.trim() || campaignStatuses.some((s, i) => i !== index && s.name?.trim().toLowerCase() === item.name?.trim().toLowerCase()) || savingStates[index]}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {!item.isNew && (
                      editingIndex === index ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            autoFocus
                            size="small"
                            variant="outlined"
                            placeholder="Rename Campaign Status"
                            className={classes.textField}
                            style={{ marginRight: 8 }}
                            inputProps={{
                              onBeforeInput: (e) => {
                                const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                                if (!allowedChars.test(e.data)) {
                                  e.preventDefault();
                                }
                              }
                            }}
                          />
                          <div className={classes.inlineActions}>
                            <IconButton onClick={() => setEditingIndex(null)} className={classes.iconBtn}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={async () => {
                                const updated = [...campaignStatuses];
                                updated[index].newStatusName = renameValue;
                                updated[index].isUpdated = true;
                                setCampaignStatuses(updated);
                                setEditingIndex(null);
                                await handleSaveChanges();
                              }}
                              className={classes.iconBtn}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {item.newStatusName || "NA"}
                          {canEdit && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingIndex(index);
                                setRenameValue(item.newStatusName || "");
                              }}
                              style={{ marginLeft: 4 }}
                            >
                              <EditIcon style={{ color: "orange" }} />
                            </IconButton>
                          )}
                        </span>
                      )
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>{item.id}</TableCell>
                  <TableCell className={classes.bodyCell}>
                    {canEdit && (item.isNew == true || item.type === "New") && (
                      <IconButton aria-label="delete" onClick={() => handleDelete(index)}>
                        <DeleteIcon className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div
          className={classes.buttonContainer}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            marginTop: "3px",
            marginLeft: "0rem",
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{
              opacity: !isUpdated || !canEdit ? 0.7 : 1,
              color: "#fff",
            }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit}
            style={{
              opacity: !isUpdated || !canEdit ? 0.8 : 1,
            }}
          >
            Reset Selection
          </Button>
        </div>
      </div>
      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}
      {canEdit && (
        <Button variant="outlined" onClick={handleAddStatus} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Campaign Status
        </Button>
      )}
    </div>
  );
};

export default CampaignStatusTab;


